import type { ReportDtoT } from '@mathflat/domain/@entities/(Content)/Report'
import type { ParentApi as DomainApi } from '@mathflat/domain/@entities/Parent/api'
import type { SchoolType } from '@mathflat/domain/@entities/SchoolSystem/schema'
import type { Entity as StudentEntity } from '@mathflat/domain/@entities/Student/dto'
import type { CalenderDateStr } from '@mathflat/shared/@common/types/string'
import type { DefaultResponse, Pagination } from '@mathflat/shared/@types/mathflatApi'

import maxios from '../utils/maxios'

type ParentRes = {
  studentIds: string[]
}

// GET /students/list?idList=...
export type ParentStudentRes = Pick<
  StudentEntity.Student,
  'id' | 'loginId' | 'name' | 'schoolName' | 'grade' | 'attendanceCode'
> & {
  schoolType: SchoolType
}

// GET /attendances/student/{id}
export type AttendanceEntry = {
  id: number
  studentId: string
  status: 'QUIT' | 'ENTER'
  enterDatetime: string
  quitDatetime: string | null
}

export type AttendanceDay = {
  date: CalenderDateStr
  attendanceList: AttendanceEntry[]
}

// GET /v2/report/custom — 도메인 GetReportCustomV2Response 재활용
export type ReportRes = ReportDtoT.GetReportCustomV2Response

// GET /report/v2/download
type ReportPdfRes = {
  presignedUrl: string
  fileName: string
}

// GET /notices/parents (도메인에 타입 없음)
export type ParentNoticeRes = {
  id: number
  teacherName: string
  type: 'NOTICE' | 'PLAN' | 'NEWS'
  title: string
  content: string
  marked: boolean
  createDatetime: string
}

const getParentRelations = async (relationId: string) => {
  const { data } = await maxios.get<DefaultResponse<ParentRes>>(`/parents/${relationId}`)
  return data
}

// 자녀 상세 정보 조회
const getStudents = async (idList: string[]): Promise<ParentStudentRes[]> => {
  const { data } = await maxios.get<DefaultResponse<ParentStudentRes[]>>('/students/list', {
    params: { idList: idList.join(',') },
  })
  return data
}

// 특정 자녀의 출석 조회 (날짜 범위, yyyy-MM-dd)
const getAttendances = async (
  studentId: string,
  startDate: string,
  endDate: string,
): Promise<AttendanceDay[]> => {
  const { data } = await maxios.get<DefaultResponse<AttendanceDay[]>>(
    `/attendances/student/${studentId}`,
    { params: { startDate, endDate } },
  )
  return data
}

// 학부모에게 공유된 보고서 목록 (페이지네이션)
const getReports = async (
  studentId: string,
  page: number,
  signal?: AbortSignal,
): Promise<Pagination<ReportRes[]>> => {
  return maxios.get<Pagination<ReportRes[]>>('/v2/report/custom', {
    params: {
      page,
      size: 30,
      openToParent: true,
      sharedToParent: true,
      studentId,
      studentGroupKey: 'STUDENT',
    },
    signal,
  })
}

// 보고서 PDF presigned URL 조회
const getReportPdfUrl = async (reportId: number): Promise<ReportPdfRes> => {
  return maxios.get<ReportPdfRes>('/report/v2/download', {
    params: { reportIds: reportId, presignedUrlType: 'PREVIEW' },
  })
}

// 학부모 공지 1회 조회 최대 건수 — 페이지네이션 UI 도입 전까지 전체 로드 용도
const DEFAULT_PARENT_NOTICE_PAGE_SIZE = 999 as const

// 학부모 공지 목록
const getNotices = async (): Promise<ParentNoticeRes[]> => {
  const res = await maxios.get<DefaultResponse<Pagination<ParentNoticeRes[]>>>('/notices/parents', {
    params: { size: DEFAULT_PARENT_NOTICE_PAGE_SIZE, page: 0, sort: 'createDatetime,desc' },
  })
  return res.data.content
}

// 학습지 난이도별 통계 (학부모 리포트)
const getWorksheetDifficultyStatistics = async (studentId: string, worksheetId: number) => {
  const { data } = await maxios.get<DefaultResponse<DomainApi.학습지_난이도별_통계.Output>>(
    `/student-history/analysis/summary/level/${studentId}/worksheets/${worksheetId}`,
  )
  return data
}

// 학습지 보충학습 필요 단원 (학부모 리포트)
const getWorksheetSupplementaryLearning = async (studentId: string, worksheetId: number) => {
  const { data } = await maxios.get<DefaultResponse<DomainApi.학습지_보충학습_단원.Output>>(
    `/student-history/analysis/summary/concept/${studentId}/worksheets/${worksheetId}`,
    { params: { sort: 'ASC' } },
  )
  return data
}

// 교재 난이도별 통계 (학부모 리포트) — 페이지 단위 progressId
const getWorkbookDifficultyStatistics = async (progressId: number) => {
  const { data } = await maxios.get<DefaultResponse<DomainApi.교재_난이도별_통계.Output>>(
    `/student-history/analysis/summary/level/progresses/${progressId}`,
  )
  return data
}

// 교재 보충학습 필요 단원 (학부모 리포트) — 페이지 단위 progressId
const getWorkbookSupplementaryLearning = async (progressId: number) => {
  const { data } = await maxios.get<DefaultResponse<DomainApi.교재_보충학습_단원.Output>>(
    `/student-history/analysis/summary/concept/progresses/${progressId}`,
    { params: { sort: 'ASC' } },
  )
  return data
}

export const parentApi = {
  getParentRelations,
  getStudents,
  getAttendances,
  getReports,
  getReportPdfUrl,
  getNotices,
  getWorksheetDifficultyStatistics,
  getWorksheetSupplementaryLearning,
  getWorkbookDifficultyStatistics,
  getWorkbookSupplementaryLearning,
}
